<template>
  <DashboardCard>
    <template #title>
      <WdsStack direction-column gap-sm>
        <WdsStack justify-space-between align-center>
          <span>{{ $t('Shared with you') }}</span>
          <WdsButtonGhost
            :href="laravelNamedRoute('frontend.jobs.shared-jobs')"
            secondary
            class="tw-items-center"
          >
            {{ $t('See all') }}
            <WdsIcon :size="16" name="chevron-right" />
          </WdsButtonGhost>
        </WdsStack>
        <WdsText secondary :text="$t('You’ve been invited to apply to these job openings.')" />
      </WdsStack>
    </template>

    <WdsStack direction-column class="tw-pb-4">
      <!-- Show Spinner While Loading -->
      <div v-if="loading">
        <LoadingList :count="1" />
      </div>

      <!-- Show No Results Message -->
      <WdsStack
        v-else-if="sharedJobs.length === 0"
        align-center
        direction-column
        justify-center
        gap-md
      >
        <WdsText secondary class="tw-text-center">
          {{ $t('No job posts are currently shared with you') }}
        </WdsText>
      </WdsStack>

      <!-- Shared Jobs List -->
      <WdsStack v-else direction-column gap-sm class="tw-max-h-[377px] tw-overflow-y-auto">
        <div v-for="jobShare in sharedJobs" :key="jobShare.id">
          <WdsStack direction-column class="job-share-card">
            <WdsStack justify-space-between align-start gap-md class="tw-w-full tw-p-4">
              <!-- Left side: Company info and job details -->
              <WdsStack direction-column gap-xs class="tw-flex-1">
                <!-- Company name -->
                <WdsStack align-center gap-sm>
                  <WdsText class="tw-font-medium">{{ jobShare.job.company.name }}</WdsText>

                  <WdsTag
                    v-if="getClosedLabel(jobShare.job.status)"
                    sm
                    error
                    data-test="shared-job-closed-tag"
                  >
                    {{ getClosedLabel(jobShare.job.status) }}
                  </WdsTag>

                  <WdsTag
                    v-if="getLocationPreferenceLabel(jobShare.job.locationPreference)"
                    sm
                    variant="secondary"
                  >
                    {{ getLocationPreferenceLabel(jobShare.job.locationPreference) }}
                  </WdsTag>

                  <WdsTag
                    v-if="getAssociationLabel(jobShare.job.association)"
                    sm
                    variant="secondary"
                  >
                    {{ getAssociationLabel(jobShare.job.association) }}
                  </WdsTag>
                </WdsStack>

                <!-- Job title -->
                <a :href="jobUrl(jobShare.job)" data-test="shared-job-link">
                  <WdsText>
                    <div class="tw-text-indigo-500 tw-cursor-pointer tw-line-clamp-1">
                      {{ jobShare.job.name }}
                    </div>
                  </WdsText>
                </a>

                <!-- Job dates -->
                <WdsText small secondary>
                  <div class="tw-font-medium">
                    {{
                      formatDateRange(
                        jobShare.job.startDate,
                        jobShare.job.endDate,
                        jobShare.job.startDateTimeframe,
                      )
                    }}
                  </div>
                </WdsText>
              </WdsStack>
            </WdsStack>
          </WdsStack>
        </div>
      </WdsStack>
    </WdsStack>
  </DashboardCard>
</template>

<script setup lang="ts">
import { WdsButtonGhost, WdsIcon, WdsStack, WdsText, WdsTag } from '@worksome/wds-components'
import moment from 'moment'
import { computed } from 'vue'
import { useQuery } from '@vue/apollo-composable'
import { graphql, useFragment } from '@/gql'
import {
  LocationPreference,
  Association,
  AccountType,
  JobStatus,
  type SharedJobFieldsFragment,
} from '@/gql/graphql'
import DashboardCard from '@/components/Dashboard/DashboardCard.vue'
import { useTranslation } from '@/composables/translation'
import LoadingList from '@/components/Dashboard/Widgets/Utils/LoadingList.vue'
import { laravelNamedRoute } from '@/utils/laravelNamedRoutes'

const props = withDefaults(
  defineProps<{
    accountTypes?: AccountType[]
  }>(),
  { accountTypes: () => [AccountType.Worker] },
)

const { $t } = useTranslation()

const SharedJobFragment = graphql(`
  fragment SharedJobFields on JobShare {
    id
    isActive
    job {
      id
      name
      description
      currency
      rateType
      startDate
      endDate
      startDateTimeframe
      locationPreference
      association
      status
      company {
        id
        name
      }
    }
  }
`)

const { result, loading } = useQuery(
  graphql(`
    query SharedWithYouJobs($accountTypes: [AccountType!]) {
      jobShares(
        first: 10
        accountTypes: $accountTypes
        isActive: true
        orderBy: [{ field: CREATED_AT, order: DESC }]
      ) {
        data {
          ...SharedJobFields
        }
      }
    }
  `),
  () => ({ accountTypes: props.accountTypes }),
  () => ({ clientId: 'publicApi' }),
)

const sharedJobs = computed(
  () =>
    result.value?.jobShares?.data?.map((jobShare) => useFragment(SharedJobFragment, jobShare)) ||
    [],
)

const getClosedLabel = (status: JobStatus | null | undefined): string | null => {
  switch (status) {
    case JobStatus.Removed:
      return $t('Removed')
    case JobStatus.Completed:
      return $t('Ended')
    default:
      return null
  }
}

// Closed jobs are only viewable by their owner, so open the conversation where submitted candidates are listed.
const jobUrl = (job: SharedJobFieldsFragment['job']): string =>
  getClosedLabel(job.status)
    ? laravelNamedRoute('frontend.job.dialog', { id: job.id })
    : laravelNamedRoute('jobs.show', { job: job.id })

const getLocationPreferenceLabel = (
  locationPreference: string | null | undefined,
): string | null => {
  if (!locationPreference) {
    return null
  }

  switch (locationPreference) {
    case LocationPreference.OnsiteOnly:
      return $t('On-site')
    case LocationPreference.OnsiteSome:
      return $t('Some onsite')
    case LocationPreference.RemoteOnly:
      return $t('Remote')
    default:
      return null
  }
}

const getAssociationLabel = (association: string | null | undefined): string | null => {
  if (!association) {
    return null
  }

  switch (association) {
    case Association.FullTime:
      return $t('Full-Time')
    case Association.PartTime:
      return $t('Part-Time')
    case Association.Project:
    case Association.BigProject:
      return $t('One-Time Project')
    case Association.SmallTask:
      return $t('Small Task')
    default:
      return null
  }
}

const getStartTimeframeLabel = (timeframe: string | null | undefined): string | null => {
  if (!timeframe) {
    return null
  }

  switch (timeframe) {
    case 'ASAP':
      return $t('As soon as possible')
    case 'NEXT_MONTH':
      return $t('Next month')
    case 'ON_DATE':
      return null // This will be handled by the actual date
    default:
      return null
  }
}

const formatDateRange = (
  startDate: string | null,
  endDate: string | null,
  startDateTimeframe: string | null = null,
): string => {
  const formatDate = (dateStr: string | null) => {
    if (!dateStr) {
      return null
    }

    return moment(dateStr).format('DD MMM YYYY')
  }

  const start = formatDate(startDate)
  const end = formatDate(endDate)

  if (start && end) {
    return `${start} - ${end}`
  }

  if (start) {
    return `${start} - ${$t('No end date')}`
  }

  if (end) {
    return end
  }

  if (startDateTimeframe) {
    const timeframeLabel = getStartTimeframeLabel(startDateTimeframe)
    if (timeframeLabel) {
      return timeframeLabel
    }
  }

  return ''
}
</script>

<style lang="scss" scoped>
.job-share-card {
  @apply tw-bg-white;
  @apply tw-rounded-lg;
  @apply tw-mb-3;
  @apply tw-border-2;
  @apply tw-border-transparent;
  @apply tw-transition;
  @apply tw-duration-200;

  &:last-child {
    @apply tw-mb-0;
  }
}

.job-share-card:hover {
  @apply tw-border-2;
  @apply tw-border-gray-200;
}
</style>
